import React, {useEffect, useState} from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Button,
    Select,
    MenuItem,
} from "@mui/material";
import Api, {APIs} from "../../Api";

export interface ITeam {
    id?: number; // Optional for new rows
    pid: string; // Project ID
    name: string;
    designation: string;
    affiliation: string; // Dropdown value
    contact_info: string[];
    phones?: string[];
    emails?: string[];
}

interface IProps {
    pid: string;
}

const EditableProjectTeam: React.FC<IProps> = ({pid}) => {
    const [teams, setTeams] = useState<ITeam[]>([]);
    const [editIdx, setEditIdx] = useState<number | null>(null);
    const [message, setMessage] = useState<{ text: string; type: "error" | "success" } | null>(null);

    // Extract Contact Details (Phones and Emails)
    const extractContactDetails = (contact_info: string[]) => {
        const emailRegex = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/gi;
        const phoneRegex = /(\+?\d{1,4}[\s.-]?)?(\(?\d{3}\)?[\s.-]?)?\d{3}[\s.-]?\d{4}/g;

        const emails: string[] = [];
        const phones: string[] = [];

        contact_info.forEach((info) => {
            const emailMatch = info.match(emailRegex);
            if (emailMatch) {
                emails.push(...emailMatch);
            }

            const phoneMatch = info.match(phoneRegex);
            if (phoneMatch) {
                phones.push(...phoneMatch);
            }
        });

        return {emails, phones};
    };

    // Fetch Team Data
    useEffect(() => {
        const url = Api.getURL(APIs.MoPD_PROJECT_TEAM, {pid});
        fetch(url)
            .then((response) => response.json())
            .then((data) => {
                const {payload} = data;
                if (payload) {
                    const processedTeams = payload.map((team: any) => {
                        const {emails, phones} = extractContactDetails(team.contact_info);
                        return {...team, emails, phones};
                    });
                    setTeams(processedTeams);
                }
            });
    }, [pid]);

    const handleEditChange = <K extends keyof ITeam>(
        index: number,
        field: K,
        value: ITeam[K]
    ) => {
        const updatedTeams = [...teams];
        updatedTeams[index][field] = value;
        setTeams(updatedTeams);
    };

    const handleEditContact = (
        index: number,
        type: "phones" | "emails",
        value: string
    ) => {
        const updatedTeams = [...teams];
        if (type === "phones") {
            updatedTeams[index].phones = value.split(",").map((v) => v.trim());
        } else {
            updatedTeams[index].emails = value.split(",").map((v) => v.trim());
        }

        // Update contact_info
        updatedTeams[index].contact_info = [
            ...(updatedTeams[index].phones || []),
            ...(updatedTeams[index].emails || []),
        ].filter((v) => v !== ""); // Remove empty strings

        setTeams(updatedTeams);
    };

    const handleSaveRow = async (index: number) => {
        const team = teams[index];

        // Validation: Ensure name, designation, and at least one phone or email are provided
        if (!team.name.trim()) {
            setMessage({text: "Name cannot be blank.", type: "error"});
            return;
        }

        if (!team.designation.trim()) {
            setMessage({text: "Designation cannot be blank.", type: "error"});
            return;
        }

        if (!team.contact_info.length) {
            setMessage({
                text: "Each team member must have at least one phone number or email.",
                type: "error",
            });
            return;
        }

        // Clear error message if validation passes
        setMessage(null);

        // Prepare payload
        const payload = {
            ...team,
            contact_info: team.contact_info, // Send only contact_info
        };
        delete payload.emails; // Exclude emails
        delete payload.phones; // Exclude phones

        const url = Api.getURL(APIs.MoPD_UPDATE_PROJECT_TEAM, {});
        const method = team.id ? "PUT" : "POST";

        try {
            const response = await fetch(url, {
                method,
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                throw new Error("Failed to save team member.");
            }

            const responseData = await response.json();

            // Update the team with the new id from the backend if it was a new row
            if (!team.id && responseData.id) {
                const updatedTeams = [...teams];
                updatedTeams[index] = {...team, id: responseData.id};
                setTeams(updatedTeams);
            }

            setMessage({text: "Team member saved successfully!", type: "success"});
            setEditIdx(null);
        } catch (error) {
            console.error(error);
            setMessage({text: "Failed to save team member.", type: "error"});
        }
    };

    const handleDeleteRow = async (index: number) => {
        const team = teams[index];

        if (team.id) {
            // Delete existing row from backend
            const url = Api.getURL(APIs.MoPD_UPDATE_PROJECT_TEAM, {});
            const response = await fetch(url, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({id: team.id}),
            });

            if (response.ok) {
                setMessage({text: "Team member deleted successfully!", type: "success"});
            } else {
                setMessage({text: "Failed to delete team member.", type: "error"});
                return; // Abort deletion from frontend state
            }
        }

        // Remove row from frontend state
        setTeams(teams.filter((_, i) => i !== index));
    };

    const handleAddRow = () => {
        setTeams([
            ...teams,
            {
                pid,
                name: "",
                designation: "",
                affiliation: "i", // Default to "Institute"
                contact_info: [],
                phones: [],
                emails: [],
            },
        ]);
        setEditIdx(teams.length); // Enter edit mode for the new row
    };

    return (
        <div className="table-container">
            <p className="TimelineText">Editable Project Team</p>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Designation</TableCell>
                            <TableCell>Affiliation</TableCell>
                            <TableCell>Phones</TableCell>
                            <TableCell>Emails</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {teams.map((team, index) => (
                            <TableRow key={team.id || index}>
                                <TableCell>
                                    {editIdx === index ? (
                                        <TextField
                                            value={team.name}
                                            onChange={(e) =>
                                                handleEditChange(index, "name", e.target.value)
                                            }
                                            placeholder="Name"
                                        />
                                    ) : (
                                        team.name
                                    )}
                                </TableCell>
                                <TableCell>
                                    {editIdx === index ? (
                                        <TextField
                                            value={team.designation}
                                            onChange={(e) =>
                                                handleEditChange(index, "designation", e.target.value)
                                            }
                                            placeholder="Designation"
                                        />
                                    ) : (
                                        team.designation
                                    )}
                                </TableCell>
                                <TableCell>
                                    {editIdx === index ? (
                                        <Select
                                            value={team.affiliation || "i"} // Default to "i"
                                            onChange={(e) =>
                                                handleEditChange(index, "affiliation", e.target.value as string)
                                            }
                                        >
                                            <MenuItem value="p">Personnel</MenuItem>
                                            <MenuItem value="i">Institute</MenuItem>
                                            <MenuItem value="o">Other</MenuItem>
                                        </Select>
                                    ) : (
                                        team.affiliation === "p"
                                            ? "Personnel"
                                            : team.affiliation === "i"
                                                ? "Institute"
                                                : "Other"
                                    )}
                                </TableCell>
                                <TableCell>
                                    {editIdx === index ? (
                                        <TextField
                                            value={(team.phones || []).join(", ")}
                                            onChange={(e) =>
                                                handleEditContact(index, "phones", e.target.value)
                                            }
                                            placeholder="Phones"
                                        />
                                    ) : team.phones && team.phones.length > 0 ? (
                                        team.phones.join(", ")
                                    ) : (
                                        "N/A"
                                    )}
                                </TableCell>
                                <TableCell>
                                    {editIdx === index ? (
                                        <TextField
                                            value={(team.emails || []).join(", ")}
                                            onChange={(e) =>
                                                handleEditContact(index, "emails", e.target.value)
                                            }
                                            placeholder="Emails"
                                        />
                                    ) : team.emails && team.emails.length > 0 ? (
                                        team.emails.join(", ")
                                    ) : (
                                        "N/A"
                                    )}
                                </TableCell>
                                <TableCell>
                                    {editIdx === index ? (
                                        <Button onClick={() => handleSaveRow(index)}>Save</Button>
                                    ) : (
                                        <Button onClick={() => setEditIdx(index)}>Edit</Button>
                                    )}
                                    <Button onClick={() => handleDeleteRow(index)}>Delete</Button>
                                </TableCell>
                            </TableRow>
                        ))}
                        <TableRow>
                            <TableCell colSpan={6} style={{textAlign: "center"}}>
                                <Button onClick={handleAddRow}>Add New Row</Button>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
            {message && (
                <div
                    style={{
                        color: message.type === "error" ? "red" : "green",
                        marginTop: "20px",
                        textAlign: "center",
                    }}
                >
                    {message.text}
                </div>
            )}
        </div>
    );
};

export default EditableProjectTeam;
